import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';
import { DestroyBase } from '../../../../../shared/base-classes/destroy-component-base';
import { LeadStatusCount } from '../../../../dashboard/models/lead-status-count';
import { LeadStatusService } from '../../services/lead-status.service';

@Component({
  selector: 'app-lead-status-logs-dashboards',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './lead-status-logs-dashboard.component.html',
  styleUrl: './lead-status-logs-dashboard.component.scss',
})
export class LeadStatusLogsDashboardComponent
  extends DestroyBase
  implements OnChanges
{
  private leadStatusService = inject(LeadStatusService);

  @Input() connectionId?: number;
  @Input() status?: number;
  @Output() leadStatusClicked = new EventEmitter<string>();

  leadStatusCounts$!: Observable<LeadStatusCount[]>;
  notDefinedCount$!: Observable<number>;
  totalLeads: number | string = '_';
  totalLeadsStatus: number | string = '-';
  isExpanded = true;
  showLeadsStatusPopup = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['connectionId'] || changes['status']) {
      this.loadLeadsStatusCounts();
    }
  }

  private loadLeadsStatusCounts(): void {
    this.leadStatusService
      .getLeadsStatusCounts(this.connectionId, this.status)
      .pipe(
        takeUntil(this.destroy$),
        tap((counts) => {
          this.totalLeads = counts.reduce((sum, count) => sum + count.count, 0);
          this.totalLeadsStatus = counts.reduce(
            (sum, count) => sum + count.count,
            0,
          );
        }),
      )
      .subscribe((counts) => {
        this.leadStatusCounts$ = new Observable<LeadStatusCount[]>(
          (observer) => {
            observer.next(
              [...counts].sort((a, b) => a.status.localeCompare(b.status)),
            );
            observer.complete();
          },
        );
      });
  }

  onLeadsStatusClick(statusLabel: string): void {
    this.leadStatusClicked.emit(statusLabel);
  }

  getStatusColorClass(status: string): string {
    // Normalize status to match CSS class format (lowercase, underscores)
    const normalizedStatus = status.toLowerCase().replace(/[^a-z0-9]/g, '_');
    return `status-${normalizedStatus}`;
  }

  getStatusColor(status: string): string {
    // Normalize status to handle various formats
    const normalizedStatus = status.toLowerCase().replace(/[^a-z0-9]/g, '_');

    const colorMap: Record<string, string> = {
      under_review: '#f59e0b', // amber
      further_information_required: '#3b82f6', // blue
      prospective_client: '#10b981', // emerald
      on_hold: '#8b5cf6', // purple
      declined: '#ef4444', // red
      not_currently_pursuing: '#6b7280', // gray
      active_client: '#059669', // green
      request_lead_contact: '#f97316', // orange
      not_defined: '#9ca3af', // light gray
      active_funded: '#0d9488', // teal
      active_funded_grant: '#15803d', // forest green
      active_technical_assistance: '#6366f1', // indigo
    };

    return colorMap[normalizedStatus] || '#6b7280';
  }

  getPercentage(count: number): number {
    return +this.totalLeadsStatus > 0
      ? Math.round((count / +this.totalLeadsStatus) * 100)
      : 0;
  }

  getCircumference(): number {
    const radius = 25;
    return 2 * Math.PI * radius;
  }

  getStrokeOffset(percentage: number): number {
    const circumference = this.getCircumference();
    return circumference - (percentage / 100) * circumference;
  }
}
